import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { PropertiesService } from 'src/app/services/properties.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminHpPropertyDialogComponent } from './admin-hp-property-dialog/admin-hp-property-dialog.component';
import { DataTableDirective } from 'angular-datatables';
import { DatatablesHelper, FormatHelper, TextHelper } from 'src/app/helpers';
import { Property } from 'src/app/models';
import { Clipboard } from '@angular/cdk/clipboard';
import { AdminService } from 'src/app/services/admin.service';
import { RouteService } from 'src/app/core/services';

type UIProperty = Property & {
  duplicates?: string[] // ids
  challenges?: string[] // ids
}

@Component({
  selector: 'app-admin-hp-properties',
  templateUrl: './admin-hp-properties.component.html',
  styleUrls: ['./admin-hp-properties.component.css']
})
export class AdminHpPropertiesComponent implements OnInit, OnDestroy {
  // readonly COLS_HIDDEN_BY_DEFAULT = []; // NOTE: keep commented out, use for local testing only
  readonly COLS_HIDDEN_BY_DEFAULT = [0, 1, 3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 26, 27, 28, 29, 30, 31, 32];
  readonly COLS_ORDER_BY_DEFAULT = [[5, 'asc']]; // by address
  readonly COLS_CONTACT_INFORMATION = [9, 10, 11, 12];

  allProperties: any[] = [];
  loaded = false;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, {static: false})
  dtElement!: DataTableDirective;

  constructor(
    private propertiesService: PropertiesService,
    private adminService: AdminService,
    private routeService: RouteService,
    private flashMessage: FlashMessagesService,
    private dialog: MatDialog,
    private clipboard: Clipboard
  ) {}

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      columnDefs: [
        {
          // Indexes of columns to be hidden by default
          "targets": this.COLS_HIDDEN_BY_DEFAULT,
          "visible": false
        }
      ],

      order: this.COLS_ORDER_BY_DEFAULT,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        // 'colvis',
        {
          extend: 'colvis',
          columns: (idx: number) => [0, 1].includes(idx) === false
        },
        'excel'
      ]
    });
  }

  private async loadTable() {
    // Get all the properties
    const query = {
      getduplicates: true,
      getchallenges: true,
    };

    const res: any = await this.propertiesService.getAllProperties(query).toPromise();

    this.allProperties = res.properties || [];

    this.toggleIssuesCol();

    if (this.loaded) {
      // Manually rerender table after changes
      this.rerender();
    } else {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  private toggleIssuesCol(): void {
    const showIssues = this.allProperties.some(property => this.issuesFoundBy(property).count > 0);

    const colsHidden = this.dtOptions.columnDefs![0];

    if (showIssues) {
      const colsToShow = [0, ...this.COLS_CONTACT_INFORMATION];

      colsHidden.targets = this.COLS_HIDDEN_BY_DEFAULT.filter(col => colsToShow.includes(col) === false);

      this.dtOptions.order = [...this.COLS_ORDER_BY_DEFAULT];
      this.dtOptions.order.unshift([1, 'desc']); // put duplicates on top
    }
    else {
      colsHidden.targets = this.COLS_HIDDEN_BY_DEFAULT;
      this.dtOptions.order = this.COLS_ORDER_BY_DEFAULT;
    }
  }

  private rerender(): void {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  editProperty(property: any){
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Edit Property',

      // Gotta send all the property data to the dialog to prefill
      date:                  property.entryDate,
      isAvailable:           property.isAvailable,
      ownerName:             property.ownerName,
      managerName:           property.managerName,
      contactPhone:          property.contactPhone,
      email:                 property.email,
      address:               property.address,
      city:                  property.city,
      zipCode:               property.zipCode,
      buildingNumber:        property.buildingNumber,
      buildingUnits:         property.buildingUnits,
      aptLic:                property.aptLic,
      availableUnits:        property.availableUnits,
      propertyNote:          property.propertyNote,
      hcvCheck:              property.hcvCheck,
      hcvNote:               property.hcvNote,
      cuCheck:               property.cuCheck,
      cuUnits:               property.cuUnits,
      cuProvider:            property.cuProvider,
      cuNote:                property.cuNote,
      lsCheck:               property.lsCheck,
      lsProvider:            property.lsProvider,
      lsUnits:               property.lsUnits,
      lsNote:                property.lsNote,
      latitude:              property.latitude,
      longitude:             property.longitude,
      buildingAdaAccessible: property.buildingAdaAccessible,
      unitAdaAccessible:     property.unitAdaAccessible,
      accessibleNote:        property.accessibleNote,
      incentiveForRentwise:  property.incentiveForRentwise,
      incentiveNote:         property.incentiveNote,
    };

    // Open dialog
    const dialogRef = this.dialog.open(AdminHpPropertyDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      this.routeService.scrollToTop();

      // Update Property
      this.propertiesService.updateProperty(property._id, propertyInfo).toPromise().then((data: any) => {

        // Rerender manually
        this.loadTable();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Property updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });
        }
      });
    });
  }

  deleteProperty(property: UIProperty){
    if (confirm(`Are you sure you want to permanently delete property at address ${property.address}?`) !== true) {
      return;
    }

    // Delete Property
    this.propertiesService.deleteProperty(property._id).toPromise().then((data: any) => {

      // Rerender manually
      this.loadTable();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show("Property deleted.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  issuesFoundBy(property: UIProperty) {
    const duplicates = this.duplicatesFoundBy(property);
    const challenges = this.challengesFoundBy(property);
    const count = duplicates + challenges;
    return { duplicates, challenges, count };
  }

  duplicatesFoundBy(property: UIProperty) {
    return property.duplicates?.length || 0;
  }

  duplicatesTooltip(property: UIProperty): string {
    const duplicates = this.duplicatesFoundBy(property);

    let result = '';

    if (duplicates > 0) {
      result += `${FormatHelper.pluralize(duplicates, 'duplicate')} found by this address. \n`;
    }

    return result;
  }

  challengesFoundBy(property: UIProperty) {
    return property.challenges?.length || 0;
  }

  challengesTooltip(property: UIProperty): string {
    const challenges = this.challengesFoundBy(property);

    let result = '';

    if (challenges > 0) {
      result += `${FormatHelper.pluralize(challenges, 'challenge')} issued by this property. \n`;
    }

    return result;
  }

  async copyGeoJsonLink() {
    const link = await this.adminService.getGeoJsonLink('properties').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  async copyGeoRssLink() {
    const link = await this.adminService.getGeoRssLink('properties').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
